import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { IMAGES, Reveal } from "./anim";

const WORK = [
  {
    id: "01",
    title: "Meridian Health",
    cat: "Managed IT + Cloud",
    year: "2025",
    img: IMAGES.servers,
    desc: "Migrated 40 clinics to a zero-downtime private cloud.",
    challenge:
      "A 40-clinic healthcare network ran on ageing on-premise servers with frequent outages, no disaster recovery, and mounting HIPAA compliance risk.",
    solution:
      "We designed and executed a phased migration to a hardened private cloud, rolled out 24/7 monitoring, automated encrypted backups, and a full disaster-recovery runbook — clinic by clinic, out of hours.",
    results: [
      { v: "0 min", l: "Downtime during migration" },
      { v: "99.99%", l: "Uptime since launch" },
      { v: "58%", l: "Lower infra cost" },
    ],
  },
  {
    id: "02",
    title: "Halcyon Logistics",
    cat: "Custom Platform",
    year: "2024",
    img: IMAGES.code,
    desc: "Real-time fleet dashboard handling 2M events / day.",
    challenge:
      "Dispatchers were juggling five disconnected tools and spreadsheets, with no live view of a 900-vehicle fleet.",
    solution:
      "We built a real-time operations platform with live telemetry ingestion, geo-fencing alerts, and a role-based dashboard — integrated directly with their existing ERP.",
    results: [
      { v: "2M+", l: "Events processed / day" },
      { v: "34%", l: "Faster dispatch" },
      { v: "5→1", l: "Tools consolidated" },
    ],
  },
  {
    id: "03",
    title: "Northwind Retail",
    cat: "Cybersecurity",
    year: "2024",
    img: IMAGES.network,
    desc: "Zero-trust rollout across 300 endpoints.",
    challenge:
      "After a near-miss ransomware incident, a national retailer needed to secure 300 endpoints across 22 stores without disrupting trade.",
    solution:
      "We implemented a zero-trust architecture, endpoint detection & response, phishing-resistant MFA, and ran staff security drills across every location.",
    results: [
      { v: "300", l: "Endpoints secured" },
      { v: "0", l: "Breaches since" },
      { v: "92%", l: "Phishing-test pass rate" },
    ],
  },
  {
    id: "04",
    title: "Atlas Manufacturing",
    cat: "Software + DevOps",
    year: "2023",
    img: IMAGES.arch,
    desc: "Automated ERP integration cutting ops time 60%.",
    challenge:
      "Manual data entry between the shop floor and the ERP created costly errors and days of lag in production reporting.",
    solution:
      "We built automated integration pipelines with CI/CD, real-time sync between machines and the ERP, and self-service dashboards for plant managers.",
    results: [
      { v: "60%", l: "Less manual ops time" },
      { v: "Real-time", l: "Production reporting" },
      { v: "99.2%", l: "Data accuracy" },
    ],
  },
];

export default function CaseStudies() {
  const [active, setActive] = useState(null); // hover preview index
  const [open, setOpen] = useState(null); // opened case study

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative bg-surface border-t border-white/10 py-24 md:py-32 px-6 md:px-10"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="relative" onMouseLeave={() => setActive(null)}>
          <AnimatePresence>
            {active !== null && (
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="pointer-events-none hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 z-20 w-[380px] h-[260px] overflow-hidden rounded-xl border border-white/15"
              >
                <img src={WORK[active].img} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-black/20" />
              </motion.div>
            )}
          </AnimatePresence>

          {WORK.map((w, idx) => (
            <Reveal key={w.id} delay={idx * 0.05}>
              <button
                data-testid={`work-item-${w.id}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => setOpen(w)}
                className="group w-full text-left border-t border-white/10 last:border-b py-8 md:py-10 grid grid-cols-12 items-center gap-4 transition-colors duration-500 hover:bg-white/[0.02]"
              >
                <span className="col-span-2 md:col-span-1 font-mono text-sm text-[#71717A] group-hover:text-accent transition-colors duration-300">
                  {w.id}
                </span>
                <div className="col-span-10 md:col-span-6">
                  <h3 className="font-heading text-3xl md:text-5xl font-medium tracking-tight group-hover:translate-x-2 transition-transform duration-500">
                    {w.title}
                  </h3>
                  <p className="text-sm text-[#A1A1AA] mt-2 md:hidden">{w.desc}</p>
                </div>
                <span className="hidden md:block col-span-3 font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1AA]">
                  {w.cat}
                </span>
                <div className="hidden md:flex col-span-2 items-center justify-end gap-4">
                  <span className="font-mono text-sm text-[#71717A]">{w.year}</span>
                  <ArrowUpRight className="h-6 w-6 text-[#71717A] group-hover:text-accent group-hover:rotate-45 transition-all duration-300" />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <CaseStudyModal work={open} onClose={() => setOpen(null)} />
    </section>
  );
}

function CaseStudyModal({ work, onClose }) {
  useEffect(() => {
    if (!work) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [work, onClose]);

  return (
    <AnimatePresence>
      {work && (
        <motion.div
          data-testid="case-study-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`${work.title} case study`}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} aria-hidden="true" />
          <motion.div
            data-testid="case-study-modal"
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-surface"
          >
            <div className="relative h-52 md:h-64 overflow-hidden">
              <img src={work.img} alt={work.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
              <button
                data-testid="case-study-close"
                onClick={onClose}
                className="absolute top-5 right-5 grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-black/40 text-white hover:border-white/50 transition-colors duration-300"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="absolute bottom-5 left-6 md:left-8">
                <div className="font-mono text-xs uppercase tracking-[0.15em] text-accent">
                  {work.cat} · {work.year}
                </div>
                <h3 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-1">
                  {work.title}
                </h3>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-8">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.15em] text-[#71717A] mb-3">The Challenge</div>
                <p className="text-[#A1A1AA] leading-relaxed">{work.challenge}</p>
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.15em] text-[#71717A] mb-3">Our Solution</div>
                <p className="text-[#A1A1AA] leading-relaxed">{work.solution}</p>
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.15em] text-[#71717A] mb-4">The Results</div>
                <div className="grid grid-cols-3 gap-4">
                  {work.results.map((r) => (
                    <div key={r.l} className="rounded-xl border border-white/10 bg-[#0A0A0B] p-4 md:p-5">
                      <div className="font-heading text-2xl md:text-4xl font-black tracking-tight text-accent">{r.v}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#71717A] mt-2">{r.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
